'use client';

import * as React from 'react';
import { memo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ProductCard } from './product-card';
import { ProductSkeleton } from './product-skeleton';
import type { ProductWithCategories } from '@/lib/api/products';

export interface ProductsGridProps {
  products: ProductWithCategories[];
  isLoading?: boolean;
  getQuantity?: (productId: number) => number;
  onAddToCart?: (product: ProductWithCategories) => void;
  onQuantityChange?: (productId: number, quantity: number) => void;
  onOpenPopup?: (product: ProductWithCategories) => void;
  skeletonCount?: number;
  className?: string;
  columns?: 1 | 2 | 3 | 4 | 5;
  /** Индекс первой секции (для приоритетной загрузки первых изображений) */
  isFirstSection?: boolean;
}

const columnClasses = {
  1: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4 single-col-grid',
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
};

// Мемоизированный wrapper для ProductCard чтобы избежать лишних ре-рендеров
interface ProductCardWrapperProps {
  product: ProductWithCategories;
  quantity: number;
  priority: boolean;
  onAddToCart?: (product: ProductWithCategories) => void;
  onQuantityChange?: (productId: number, quantity: number) => void;
  onOpenPopup?: (product: ProductWithCategories) => void;
}

const ProductCardWrapper = memo<ProductCardWrapperProps>(
  ({ product, quantity, priority, onAddToCart, onQuantityChange, onOpenPopup }) => {
    // Стабильные обработчики
    const handleAddToCart = useCallback(() => {
      onAddToCart?.(product);
    }, [onAddToCart, product]);
    
    const handleOpenPopup = useCallback(() => {
      onOpenPopup?.(product);
    }, [onOpenPopup, product]);

    return (
      <ProductCard
        product={{
          id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          spiciness: product.spiciness,
          isNew: product.is_new,
          isPopular: product.is_popular,
          isRiceless: product.is_riceless,
          isScorched: product.is_scorched,
          isMini: product.is_mini,
          isAvailable: product.status === 'available',
        }}
        quantity={quantity}
        priority={priority}
        onAddToCart={handleAddToCart}
        onQuantityChange={onQuantityChange}
        onOpenPopup={handleOpenPopup}
      />
    );
  },
  // Custom comparison - ре-рендерим только при изменении значимых пропсов
  (prevProps, nextProps) => {
    return (
      prevProps.quantity === nextProps.quantity &&
      prevProps.product.id === nextProps.product.id &&
      prevProps.product.price === nextProps.product.price &&
      prevProps.product.status === nextProps.product.status &&
      prevProps.priority === nextProps.priority
    );
  }
);
ProductCardWrapper.displayName = 'ProductCardWrapper';

export function ProductsGrid({
  products,
  isLoading = false,
  getQuantity,
  onAddToCart,
  onQuantityChange,
  onOpenPopup,
  skeletonCount = 8,
  className,
  columns = 4,
  isFirstSection = false,
}: ProductsGridProps) {
  // Show skeletons while loading
  if (isLoading) {
    return (
      <div className={cn('grid gap-4', columnClasses[columns], className)}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <ProductSkeleton key={index} />
        ))}
      </div>
    );
  }

  // Empty state
  if (!products.length) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--text-secondary)] text-lg">
          Товары не найдены
        </p>
      </div>
    );
  }

  // Fast grid without heavy animations
  return (
    <div className={cn('grid gap-4', columnClasses[columns], className)}>
      {products.map((product, index) => (
        <ProductCardWrapper
          key={product.id}
          product={product}
          quantity={getQuantity?.(product.id) || 0}
          priority={isFirstSection && index < 8}
          onAddToCart={onAddToCart}
          onQuantityChange={onQuantityChange}
          onOpenPopup={onOpenPopup}
        />
      ))}
    </div>
  );
}
